<script setup lang="ts">
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import BaseIcon from '@/components/base/BaseIcon.vue';

interface CostBreakdownItem {
  label: string;
  value: string;
  helper?: string;
  emphasis?: boolean;
}

interface CostFormula {
  leftLabel: string;
  leftValue: string;
  operatorLabel: string;
  rightLabel: string;
  rightValue: string;
  badge?: string;
  resultLabel: string;
  resultValue: string;
  helper?: string;
}

withDefaults(defineProps<{
  visible: boolean;
  title?: string;
  messagePrefix: string;
  highlightedText?: string | null;
  messageSuffix?: string;
  costLabel?: string | null;
  costFormula?: CostFormula | null;
  costBreakdown?: CostBreakdownItem[];
  costError?: string | null;
  costLoading?: boolean;
  confirmDisabled?: boolean;
  showCost?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
}>(), {
  title: 'Confirmar Solicitação',
  highlightedText: null,
  messageSuffix: '.',
  costLabel: null,
  costFormula: null,
  costBreakdown: () => [],
  costError: null,
  costLoading: false,
  confirmDisabled: false,
  showCost: true,
  confirmLabel: 'Confirmar',
  cancelLabel: 'Cancelar',
});

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'confirm'): void;
  (e: 'cancel'): void;
}>();

const handleCancel = () => {
  emit('cancel');
  emit('update:visible', false);
};

const handleConfirm = () => {
  emit('confirm');
  emit('update:visible', false);
};
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="emit('update:visible', $event)"
    modal
    :header="title"
    class="w-[560px] max-w-[calc(100vw-2rem)]"
    :closable="true"
    :draggable="false"
  >
    <div class="space-y-5 py-2">
      <div class="flex items-start gap-4">
        <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100">
          <BaseIcon size="lg">info</BaseIcon>
        </div>
        <div class="min-w-0">
          <p class="text-sm leading-6 text-surface-700">
            {{ messagePrefix }}
            <strong v-if="highlightedText" class="font-semibold text-surface-950">{{ highlightedText }}</strong>{{ messageSuffix }}
          </p>
        </div>
      </div>

      <div
        v-if="showCost"
        class="rounded-xl border px-4 py-4 text-sm"
        :class="costError || (!costLoading && !costLabel && !costBreakdown.length) ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-900'"
      >
        <div class="flex items-start gap-3">
          <div
            class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
            :class="costError ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'"
          >
            <BaseIcon>toll</BaseIcon>
          </div>
          <div class="min-w-0 flex-1">
            <div class="text-sm font-semibold text-surface-950">
              Estimativa de tokens
            </div>

            <div v-if="costLoading" class="mt-2 text-sm font-medium">
              Calculando custo...
            </div>

            <div v-else-if="costFormula || costBreakdown.length" class="mt-3 space-y-3">
              <div
                v-if="costFormula"
                class="overflow-hidden rounded-xl border border-amber-200 bg-white shadow-sm"
              >
                <div class="bg-amber-100 px-4 py-5">
                  <div class="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
                    <div class="text-sm font-semibold uppercase tracking-[0.12em] text-amber-800">
                      {{ costFormula.resultLabel }}
                    </div>
                    <div class="text-3xl font-bold leading-none text-surface-950">
                      {{ costFormula.resultValue }}
                    </div>
                  </div>
                  <p v-if="costFormula.helper" class="mt-2 text-xs leading-5 text-amber-700">
                    {{ costFormula.helper }}
                  </p>
                </div>

                <div class="grid gap-0 border-t border-amber-100 bg-white/80 sm:grid-cols-[1fr_auto_1.25fr]">
                  <div class="flex flex-col justify-center px-4 py-4">
                    <div class="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-500">
                      {{ costFormula.leftLabel }}
                    </div>
                    <div class="mt-1 text-2xl font-semibold leading-none text-surface-800">
                      {{ costFormula.leftValue }}
                    </div>
                  </div>

                  <div class="flex items-center justify-center px-2 py-0 sm:py-4">
                    <span class="rounded-full bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-600">
                      {{ costFormula.operatorLabel }}
                    </span>
                  </div>

                  <div class="flex flex-col justify-center px-4 py-4">
                    <div class="text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-500">
                      {{ costFormula.rightLabel }}
                    </div>
                    <div class="mt-1 text-2xl font-semibold leading-none text-surface-800">
                      {{ costFormula.rightValue }}
                    </div>
                    <p v-if="costFormula.badge" class="mt-2 text-xs font-medium leading-5 text-emerald-700">
                      {{ costFormula.badge }}
                    </p>
                  </div>
                </div>
              </div>

              <div v-if="costBreakdown.length" class="overflow-hidden rounded-lg border border-amber-200 bg-white/80">
                <div
                  v-for="item in costBreakdown"
                  :key="item.label"
                  class="border-b border-amber-100 px-4 py-3 last:border-b-0"
                  :class="item.emphasis ? 'bg-amber-100/70' : ''"
                >
                  <div class="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                    <span class="font-medium text-surface-700">{{ item.label }}</span>
                    <strong
                      class="text-left font-semibold text-surface-950 sm:text-right"
                      :class="item.emphasis ? 'text-lg leading-6' : ''"
                    >
                      {{ item.value }}
                    </strong>
                  </div>
                  <p v-if="item.helper" class="mt-1 text-xs leading-5 text-amber-700">
                    {{ item.helper }}
                  </p>
                </div>
              </div>
            </div>

            <p v-else-if="costLabel" class="mt-2">
              Esta operação consumirá <strong>{{ costLabel }}</strong>.
            </p>
            <p v-else class="mt-2">
              {{ costError || 'Não foi possível carregar o custo desta operação.' }}
            </p>
          </div>
        </div>
      </div>

      <!-- Optional extra content (e.g. an auto-decision opt-in), injected by the caller. -->
      <slot name="extra" />
    </div>
    <template #footer>
      <div class="w-full space-y-3">
        <slot name="footer-extra" />
        <div class="flex justify-end gap-2">
          <Button :label="cancelLabel" @click="handleCancel" text>
            <template #icon>
              <BaseIcon>close</BaseIcon>
            </template>
          </Button>
          <Button
            :label="confirmLabel"
            :disabled="confirmDisabled"
            @click="handleConfirm"
          >
            <template #icon>
              <BaseIcon>check</BaseIcon>
            </template>
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>
